import { useToggle } from '@hivebrite/react-hooks';

import { AI_FORM_CONFIG } from 'hb-react/shared/components/AIFormWidget/constants';
import { type AIFormWidgetProps } from 'hb-react/shared/components/AIFormWidget/types';

import { AIFormPanel } from './AIFormPanel';
import { useAIFormWidget } from './hooks/useAIFormWidget';
import { ButtonTrigger, FloatingTrigger } from './triggers';

export const AIFormWidget = (props: AIFormWidgetProps) => {
  const { enabledFields, placeholder, smartSuggestions } = props;
  const { open, isOpen, close } = useToggle();

  const {
    onSubmit,
    suggestions,
    updateSuggestion,
    isEnabled,
    initialValues,
    acceptSuggestion,
    rejectSuggestion,
    acceptAllSuggestions,
    resetSuggestions,
    parentFormikContext,
  } = useAIFormWidget({
    enabledFields,
  });

  if (!isEnabled) {
    return null;
  }

  if (!isOpen) {
    return <Trigger {...props} open={open} />;
  }

  return (
    <AIFormPanel
      onClose={close}
      onSubmit={onSubmit}
      suggestions={suggestions}
      acceptSuggestion={acceptSuggestion}
      rejectSuggestion={rejectSuggestion}
      updateSuggestion={updateSuggestion}
      acceptAllSuggestions={acceptAllSuggestions}
      resetSuggestions={resetSuggestions}
      isSubmitting={false}
      enabledFields={enabledFields}
      formik={parentFormikContext}
      initialValues={initialValues}
      placeholder={placeholder}
      smartSuggestions={smartSuggestions}
    />
  );
};

const isFloatingMode = (
  props: AIFormWidgetProps,
): props is Extract<AIFormWidgetProps, { mode: 'floating' }> =>
  props.mode === 'floating' || props.mode === undefined;

const isButtonMode = (
  props: AIFormWidgetProps,
): props is Extract<AIFormWidgetProps, { mode: 'button' }> =>
  props.mode === 'button';

const isCustomMode = (
  props: AIFormWidgetProps,
): props is Extract<AIFormWidgetProps, { mode: 'custom' }> =>
  props.mode === 'custom';

type TriggerProps = AIFormWidgetProps & {
  open: () => void;
};

const Trigger = (props: TriggerProps) => {
  const { open } = props;

  if (isFloatingMode(props)) {
    const {
      position = AI_FORM_CONFIG.DEFAULT_POSITION,
      offset = AI_FORM_CONFIG.DEFAULT_OFFSET,
    } = props;

    return (
      <FloatingTrigger position={position} offset={offset} onClick={open} />
    );
  }

  if (isButtonMode(props)) {
    const { buttonProps = {} } = props;

    return <ButtonTrigger onClick={open} {...buttonProps} />;
  }

  if (isCustomMode(props)) {
    const { trigger } = props;
    return trigger({ onClick: open });
  }

  return null;
};
